import React, { useState, useEffect } from 'react';
import {
  Wallet,
  RefreshCw,
  Eye,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Snowflake,
  Play,
  DollarSign,
  AlertTriangle,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Loader
} from 'lucide-react';
import './WalletManagement.css';

// NGN formatter
const formatNGN = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const CATEGORY_LABELS = {
  FUNDING: 'Wallet Funding',
  CHOWDECK_DELIVERY: 'Chowdeck Delivery',
  REFUND: 'Refund',
  ADJUSTMENT: 'Adjustment',
  REVERSAL: 'Reversal',
  PROMOTIONAL_CREDIT: 'Promotional Credit'
};

const WalletManagement = () => {
  const [wallets, setWallets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selected wallet for details
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletDetails, setWalletDetails] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [freezeReason, setFreezeReason] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    minBalance: '',
    maxBalance: ''
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Load wallets
  const loadWallets = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.minBalance) params.append('minBalance', filters.minBalance);
      if (filters.maxBalance) params.append('maxBalance', filters.maxBalance);

      const response = await fetch(`/api/admin/wallets?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setWallets(data.wallets || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotal(data.pagination?.total || 0);
      } else {
        setError(data.error || 'Failed to load wallets');
      }
    } catch (err) {
      setError('Failed to load wallets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await fetch('/api/admin/wallets/stats', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Load wallet details
  const loadWalletDetails = async (walletId) => {
    try {
      setLoadingDetails(true);
      const response = await fetch(`/api/admin/wallets/${walletId}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setWalletDetails(data.wallet);
      }

      // Load transactions
      const txResponse = await fetch(`/api/admin/wallets/${walletId}/transactions?limit=20`, {
        credentials: 'include'
      });
      const txData = await txResponse.json();

      if (txData.success) {
        setWalletTransactions(txData.transactions || []);
      }
    } catch (err) {
      console.error('Failed to load wallet details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Freeze wallet
  const handleFreeze = async () => {
    if (!freezeReason.trim()) {
      setError('Please provide a reason for freezing');
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/wallets/${selectedWallet.id}/freeze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: freezeReason })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Wallet frozen successfully');
        setShowFreezeModal(false);
        setFreezeReason('');
        loadWallets();
        loadStats();
      } else {
        setError(data.error || 'Failed to freeze wallet');
      }
    } catch (err) {
      setError('Failed to freeze wallet');
    } finally {
      setActionLoading(false);
    }
  };

  // Unfreeze wallet
  const handleUnfreeze = async (wallet) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/wallets/${wallet.id}/unfreeze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Wallet unfrozen successfully');
        loadWallets();
        loadStats();
      } else {
        setError(data.error || 'Failed to unfreeze wallet');
      }
    } catch (err) {
      setError('Failed to unfreeze wallet');
    } finally {
      setActionLoading(false);
    }
  };

  // Adjust wallet balance
  const handleAdjust = async () => {
    const amount = parseFloat(adjustAmount);
    if (!amount || amount === 0) {
      setError('Please enter a valid adjustment amount');
      return;
    }
    if (!adjustReason.trim()) {
      setError('Please provide a reason for the adjustment');
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/wallets/${selectedWallet.id}/adjust`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason: adjustReason })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(`Wallet ${amount > 0 ? 'credited' : 'debited'} successfully`);
        setShowAdjustModal(false);
        setAdjustAmount('');
        setAdjustReason('');
        loadWallets();
        loadStats();
      } else {
        setError(data.error || 'Failed to adjust wallet');
      }
    } catch (err) {
      setError('Failed to adjust wallet');
    } finally {
      setActionLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadWallets();
    loadStats();
  }, []);

  // Reload on filter/page change
  useEffect(() => {
    loadWallets();
  }, [page, filters]);

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const openDetailsModal = (wallet) => {
    setSelectedWallet(wallet);
    setShowDetailsModal(true);
    loadWalletDetails(wallet.id);
  };

  const openFreezeModal = (wallet) => {
    setSelectedWallet(wallet);
    setShowFreezeModal(true);
  };

  const openAdjustModal = (wallet) => {
    setSelectedWallet(wallet);
    setShowAdjustModal(true);
  };

  return (
    <div className="wm-container">
      <div className="wm-header">
        <div className="wm-header-left">
          <Wallet className="wm-header-icon" />
          <div>
            <h1>Wallet Management</h1>
            <p>Manage merchant wallets and transactions</p>
          </div>
        </div>
        <button className="wm-btn wm-btn-secondary" onClick={() => { loadWallets(); loadStats(); }}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Messages */}
      {error && <div className="wm-message wm-message-error">{error}</div>}
      {success && <div className="wm-message wm-message-success">{success}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className="wm-stats-grid">
          <div className="wm-stat-card">
            <div className="wm-stat-icon wm-stat-icon-blue">
              <Users size={20} />
            </div>
            <div className="wm-stat-content">
              <span className="wm-stat-label">Total Wallets</span>
              <span className="wm-stat-value">{stats.totalWallets || 0}</span>
            </div>
          </div>
          <div className="wm-stat-card">
            <div className="wm-stat-icon wm-stat-icon-green">
              <DollarSign size={20} />
            </div>
            <div className="wm-stat-content">
              <span className="wm-stat-label">Total Balance</span>
              <span className="wm-stat-value">{formatNGN(stats.totalBalance)}</span>
            </div>
          </div>
          <div className="wm-stat-card">
            <div className="wm-stat-icon wm-stat-icon-purple">
              <TrendingUp size={20} />
            </div>
            <div className="wm-stat-content">
              <span className="wm-stat-label">Total Funded</span>
              <span className="wm-stat-value">{formatNGN(stats.totalFunded)}</span>
            </div>
          </div>
          <div className="wm-stat-card">
            <div className="wm-stat-icon wm-stat-icon-orange">
              <AlertTriangle size={20} />
            </div>
            <div className="wm-stat-content">
              <span className="wm-stat-label">Low Balance</span>
              <span className="wm-stat-value">{stats.lowBalanceWallets || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="wm-filters">
        <div className="wm-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by email, store name, or wallet ref..."
            value={filters.search}
            onChange={(e) => {
              setFilters(f => ({ ...f, search: e.target.value }));
              setPage(1);
            }}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => {
            setFilters(f => ({ ...f, status: e.target.value }));
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="FROZEN">Frozen</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
        <input
          type="number"
          placeholder="Min Balance"
          value={filters.minBalance}
          onChange={(e) => {
            setFilters(f => ({ ...f, minBalance: e.target.value }));
            setPage(1);
          }}
          className="wm-filter-input"
        />
        <input
          type="number"
          placeholder="Max Balance"
          value={filters.maxBalance}
          onChange={(e) => {
            setFilters(f => ({ ...f, maxBalance: e.target.value }));
            setPage(1);
          }}
          className="wm-filter-input"
        />
      </div>

      {/* Wallets Table */}
      <div className="wm-table-container">
        {loading ? (
          <div className="wm-loading">
            <Loader className="wm-spinner" size={24} />
            Loading wallets...
          </div>
        ) : wallets.length === 0 ? (
          <div className="wm-empty">
            <Wallet size={48} />
            <p>No wallets found</p>
          </div>
        ) : (
          <table className="wm-table">
            <thead>
              <tr>
                <th>Wallet Ref</th>
                <th>Merchant</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Transactions</th>
                <th>Last Activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {wallets.map((wallet) => (
                <tr key={wallet.id}>
                  <td className="wm-wallet-ref">{wallet.walletRef}</td>
                  <td>
                    <div className="wm-merchant-info">
                      <span className="wm-merchant-name">{wallet.merchant?.store?.name || 'N/A'}</span>
                      <span className="wm-merchant-email">{wallet.merchant?.email}</span>
                    </div>
                  </td>
                  <td className={`wm-balance ${wallet.isLowBalance ? 'wm-low-balance' : ''}`}>
                    {formatNGN(wallet.balance)}
                    {wallet.isLowBalance && <AlertTriangle size={14} />}
                  </td>
                  <td>
                    <span className={`wm-status wm-status-${wallet.status.toLowerCase()}`}>
                      {wallet.status}
                    </span>
                  </td>
                  <td>{wallet.transactionCount || 0}</td>
                  <td>
                    {wallet.lastFundedAt
                      ? new Date(wallet.lastFundedAt).toLocaleDateString('en-NG', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : 'Never'}
                  </td>
                  <td>
                    <div className="wm-actions">
                      <button
                        className="wm-action-btn"
                        onClick={() => openDetailsModal(wallet)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      {wallet.status === 'ACTIVE' ? (
                        <button
                          className="wm-action-btn wm-action-freeze"
                          onClick={() => openFreezeModal(wallet)}
                          title="Freeze Wallet"
                        >
                          <Snowflake size={16} />
                        </button>
                      ) : wallet.status === 'FROZEN' ? (
                        <button
                          className="wm-action-btn wm-action-unfreeze"
                          onClick={() => handleUnfreeze(wallet)}
                          title="Unfreeze Wallet"
                          disabled={actionLoading}
                        >
                          <Play size={16} />
                        </button>
                      ) : null}
                      <button
                        className="wm-action-btn wm-action-adjust"
                        onClick={() => openAdjustModal(wallet)}
                        title="Adjust Balance"
                      >
                        <DollarSign size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="wm-pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          <span>Page {page} of {totalPages} ({total} wallets)</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="wm-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="wm-modal wm-modal-large" onClick={e => e.stopPropagation()}>
            <div className="wm-modal-header">
              <h2>Wallet Details</h2>
              <button onClick={() => setShowDetailsModal(false)}>&times;</button>
            </div>
            <div className="wm-modal-body">
              {loadingDetails ? (
                <div className="wm-loading">
                  <Loader className="wm-spinner" size={24} />
                  Loading...
                </div>
              ) : walletDetails ? (
                <>
                  <div className="wm-detail-grid">
                    <div className="wm-detail-item">
                      <label>Wallet Ref</label>
                      <span>{walletDetails.walletRef}</span>
                    </div>
                    <div className="wm-detail-item">
                      <label>Status</label>
                      <span className={`wm-status wm-status-${walletDetails.status.toLowerCase()}`}>
                        {walletDetails.status}
                      </span>
                    </div>
                    <div className="wm-detail-item">
                      <label>Balance</label>
                      <span className="wm-detail-balance">{formatNGN(walletDetails.balance)}</span>
                    </div>
                    <div className="wm-detail-item">
                      <label>Total Funded</label>
                      <span>{formatNGN(walletDetails.totalFunded)}</span>
                    </div>
                    <div className="wm-detail-item">
                      <label>Total Debited</label>
                      <span>{formatNGN(walletDetails.totalDebited)}</span>
                    </div>
                    <div className="wm-detail-item">
                      <label>Transactions</label>
                      <span>{walletDetails.transactionCount}</span>
                    </div>
                  </div>

                  <h3>Recent Transactions</h3>
                  {walletTransactions.length === 0 ? (
                    <p className="wm-no-transactions">No transactions yet</p>
                  ) : (
                    <div className="wm-transactions-list">
                      {walletTransactions.map((tx) => (
                        <div key={tx.id} className={`wm-transaction-item wm-tx-${tx.type.toLowerCase()}`}>
                          <div className="wm-tx-icon">
                            {tx.type === 'CREDIT' ? (
                              <ArrowUpRight size={16} />
                            ) : (
                              <ArrowDownRight size={16} />
                            )}
                          </div>
                          <div className="wm-tx-details">
                            <span className="wm-tx-description">{tx.description}</span>
                            <span className="wm-tx-meta">
                              {CATEGORY_LABELS[tx.category] || tx.category} •{' '}
                              {new Date(tx.createdAt).toLocaleString('en-NG')}
                            </span>
                          </div>
                          <div className={`wm-tx-amount wm-tx-amount-${tx.type.toLowerCase()}`}>
                            {tx.type === 'CREDIT' ? '+' : '-'}
                            {formatNGN(tx.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Freeze Modal */}
      {showFreezeModal && (
        <div className="wm-modal-overlay" onClick={() => setShowFreezeModal(false)}>
          <div className="wm-modal" onClick={e => e.stopPropagation()}>
            <div className="wm-modal-header">
              <h2>Freeze Wallet</h2>
              <button onClick={() => setShowFreezeModal(false)}>&times;</button>
            </div>
            <div className="wm-modal-body">
              <p>
                You are about to freeze wallet <strong>{selectedWallet?.walletRef}</strong>.
                This will prevent all transactions.
              </p>
              <div className="wm-form-group">
                <label>Reason for freezing *</label>
                <textarea
                  value={freezeReason}
                  onChange={(e) => setFreezeReason(e.target.value)}
                  placeholder="Enter reason..."
                  rows={3}
                />
              </div>
            </div>
            <div className="wm-modal-footer">
              <button
                className="wm-btn wm-btn-secondary"
                onClick={() => setShowFreezeModal(false)}
              >
                Cancel
              </button>
              <button
                className="wm-btn wm-btn-danger"
                onClick={handleFreeze}
                disabled={actionLoading}
              >
                {actionLoading ? 'Freezing...' : 'Freeze Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Modal */}
      {showAdjustModal && (
        <div className="wm-modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="wm-modal" onClick={e => e.stopPropagation()}>
            <div className="wm-modal-header">
              <h2>Adjust Wallet Balance</h2>
              <button onClick={() => setShowAdjustModal(false)}>&times;</button>
            </div>
            <div className="wm-modal-body">
              <p>
                Adjust balance for wallet <strong>{selectedWallet?.walletRef}</strong>.
                Current balance: <strong>{formatNGN(selectedWallet?.balance)}</strong>
              </p>
              <div className="wm-form-group">
                <label>Adjustment Amount (NGN) *</label>
                <input
                  type="number"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  placeholder="Positive to credit, negative to debit"
                />
                <span className="wm-hint">Use positive number to credit, negative to debit</span>
              </div>
              <div className="wm-form-group">
                <label>Reason for adjustment *</label>
                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Enter reason..."
                  rows={3}
                />
              </div>
            </div>
            <div className="wm-modal-footer">
              <button
                className="wm-btn wm-btn-secondary"
                onClick={() => setShowAdjustModal(false)}
              >
                Cancel
              </button>
              <button
                className="wm-btn wm-btn-primary"
                onClick={handleAdjust}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Apply Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;
